import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDatepicker } from '@angular/material/datepicker';
import { TaskActionType } from '../services/tasks.service';
import {
  buildHourOptions,
  buildMinuteOptions,
  combineDateAndTimePartsToIso,
  defaultScheduleDateTime,
  formatScheduleDateLabel,
  TimeMeridiem,
} from '../utils/datetime.util';

export type TaskActionDialogMode = TaskActionType | 'complete';
export type ApproveExecutionTiming = 'now' | 'scheduled';

export interface TaskActionDialogData {
  mode: TaskActionDialogMode;
  taskTitle?: string;
  leadName?: string;
  scheduledAt?: string;
}

export interface TaskActionDialogResult {
  confirmed: boolean;
  reason?: string;
  when?: string;
  scheduled_at?: string;
}

@Component({
  selector: 'hour4u-admin-task-action-dialog',
  templateUrl: './task-action-dialog.component.html',
  styleUrls: ['./task-action-dialog.component.scss']
})
export class TaskActionDialogComponent implements OnInit {
  @ViewChild('approveDatePicker', { static: false }) approveDatePicker: MatDatepicker<Date>;

  reason = '';
  isLoading = false;
  executionTiming: ApproveExecutionTiming = 'now';
  scheduledDate: Date | null = null;
  timeHour = '09';
  timeMinute = '00';
  timeMeridiem: TimeMeridiem = 'AM';
  hours = buildHourOptions();
  minutes = buildMinuteOptions();

  constructor(
    private dialogRef: MatDialogRef<TaskActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TaskActionDialogData,
  ) { }

  ngOnInit() {
    if (this.showExecutionTiming) {
      const defaults = defaultScheduleDateTime(2);
      this.scheduledDate = defaults.date;
      this.timeHour = defaults.parts.hour;
      this.timeMinute = defaults.parts.minute;
      this.timeMeridiem = defaults.parts.meridiem;
    }
  }

  get scheduledDateLabel(): string {
    return formatScheduleDateLabel(this.scheduledDate);
  }

  openDatePicker() {
    if (this.approveDatePicker) {
      this.approveDatePicker.open();
    }
  }

  get dialogTitle(): string {
    switch (this.data.mode) {
      case 'approve': return 'Approve task';
      case 'reject': return 'Reject task';
      case 'pause': return 'Pause task';
      case 'cancel': return 'Delete task';
      case 'complete': return 'Mark task complete';
      default: return 'Confirm action';
    }
  }

  get isScheduledTimePassed(): boolean {
    if (!this.data.scheduledAt) {
      return false;
    }
    const scheduled = new Date(this.data.scheduledAt);
    return !isNaN(scheduled.getTime()) && scheduled.getTime() < Date.now();
  }

  get showExecutionTiming(): boolean {
    return this.data.mode === 'approve' && this.isScheduledTimePassed;
  }

  get dialogMessage(): string {
    const task = this.data.taskTitle ? `"${this.data.taskTitle}"` : 'this task';
    const lead = this.data.leadName ? ` for ${this.data.leadName}` : '';
    switch (this.data.mode) {
      case 'approve':
        if (this.showExecutionTiming) {
          return `This task was scheduled for ${this.formatScheduledAt()} and is still awaiting approval. Choose when it should run after approval.`;
        }
        return `Approve ${task}${lead}? The task will proceed as scheduled.`;
      case 'reject':
        return `Reject ${task}${lead}? You can optionally add a reason below.`;
      case 'pause':
        return `Pause ${task}${lead}? You can optionally add a reason below.`;
      case 'cancel':
        return `Delete ${task}${lead}? It will be cancelled and moved to closed tasks.`;
      case 'complete':
        return `Mark ${task}${lead} as complete. Please provide a reason so the AI has context to continue.`;
      default:
        return '';
    }
  }

  get reasonLabel(): string {
    return this.data.mode === 'complete' ? 'Reason for completion*' : 'Reason (optional)';
  }

  get reasonPlaceholder(): string {
    if (this.data.mode === 'complete') {
      return 'E.g., brochure sent, callback completed';
    }
    if (this.data.mode === 'reject') {
      return 'E.g., duplicate task, wrong lead';
    }
    if (this.data.mode === 'cancel') {
      return 'E.g., no longer needed, scheduled by mistake';
    }
    return 'E.g., waiting for client callback';
  }

  get confirmLabel(): string {
    switch (this.data.mode) {
      case 'approve': return 'Approve';
      case 'reject': return 'Reject';
      case 'pause': return 'Pause';
      case 'cancel': return 'Delete';
      case 'complete': return 'Mark complete';
      default: return 'Confirm';
    }
  }

  get showReasonField(): boolean {
    return this.data.mode !== 'approve';
  }

  get isReasonRequired(): boolean {
    return this.data.mode === 'complete';
  }

  get canConfirm(): boolean {
    if (this.isReasonRequired && !this.reason.trim()) {
      return false;
    }
    if (this.showExecutionTiming && this.executionTiming === 'scheduled') {
      return !!combineDateAndTimePartsToIso(this.scheduledDate, {
        hour: this.timeHour,
        minute: this.timeMinute,
        meridiem: this.timeMeridiem,
      });
    }
    return true;
  }

  close() {
    this.dialogRef.close({ confirmed: false } as TaskActionDialogResult);
  }

  confirm() {
    if (!this.canConfirm) {
      return;
    }

    const result: TaskActionDialogResult = {
      confirmed: true,
      reason: this.reason.trim() || undefined,
    };

    if (this.showExecutionTiming) {
      if (this.executionTiming === 'now') {
        result.when = 'now';
      } else {
        result.scheduled_at = combineDateAndTimePartsToIso(this.scheduledDate, {
          hour: this.timeHour,
          minute: this.timeMinute,
          meridiem: this.timeMeridiem,
        });
      }
    }

    this.dialogRef.close(result);
  }

  private formatScheduledAt(): string {
    if (!this.data.scheduledAt) {
      return 'the original time';
    }
    const d = new Date(this.data.scheduledAt);
    if (isNaN(d.getTime())) {
      return this.data.scheduledAt;
    }
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return `${d.getDate()} ${months[d.getMonth()]}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}
